import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Globe, Check } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '../utils/tokenManager';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback list shown immediately while we fetch the live one from the API.
const FALLBACK_CODES = ['USD', 'EUR', 'GBP', 'NGN', 'KES', 'GHS', 'ZAR', 'CAD', 'AUD', 'JPY', 'INR', 'CHF', 'SGD', 'HKD', 'AED'];

const DisplayCurrencySetting = ({ user, onUserUpdate }) => {
  const [current, setCurrent] = useState((user?.display_currency || 'USD').toUpperCase());
  const [selected, setSelected] = useState(current);
  const [codes, setCodes] = useState(FALLBACK_CODES);
  const [saving, setSaving] = useState(false);
  const [fxMeta, setFxMeta] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchCurrencies = async () => {
      try {
        const token = getToken();
        const r = await fetch(`${BACKEND_URL}/api/fx/supported-currencies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const j = await r.json();
        if (cancelled) return;
        if (Array.isArray(j.currencies) && j.currencies.length > 0) setCodes(j.currencies);
      } catch (e) {
        logger.warn('Could not load supported currencies:', e);
      }
    };
    const fetchMeta = async () => {
      try {
        const token = getToken();
        const r = await fetch(`${BACKEND_URL}/api/fx/rates`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) { setFxMeta({ available: false }); return; }
        const j = await r.json();
        if (cancelled) return;
        setFxMeta({ available: true, source: j.source, fetched_at: j.fetched_at });
      } catch (_e) {
        setFxMeta({ available: false });
      }
    };
    fetchCurrencies();
    fetchMeta();
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    if (selected === current) return;
    setSaving(true);
    try {
      const token = getToken();
      const r = await fetch(`${BACKEND_URL}/api/users/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ display_currency: selected }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setCurrent(selected);
      toast.success(`Display currency updated to ${selected}.`);
      if (typeof onUserUpdate === 'function') onUserUpdate();
      // Reload to refresh all totals across the dashboard.
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      logger.error('Failed to update display currency:', e);
      toast.error('Could not save display currency. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC', timeZoneName: 'short' }); }
    catch { return iso; }
  };

  return (
    <Card className="portfolio-card" data-testid="display-currency-setting">
      <CardHeader>
        <CardTitle className="text-base sm:text-xl font-serif flex items-center gap-2">
          <Globe className="w-5 h-5 text-blue-600" />
          Display Currency
        </CardTitle>
        <p className="text-xs sm:text-sm text-slate-500 mt-1">
          Choose the single currency we use when adding up all your investments. Each holding is converted from its native currency using live exchange rates.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1">
            <label htmlFor="display-currency-select" className="text-xs font-medium text-slate-600 block mb-1">
              Currency
            </label>
            <select
              id="display-currency-select"
              data-testid="display-currency-select"
              value={selected}
              onChange={(e) => setSelected(e.target.value.toUpperCase())}
              className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 bg-white text-slate-700"
            >
              {codes.map((c) => (
                <option key={c} value={c}>{c}{c === current ? '  (current)' : ''}</option>
              ))}
            </select>
          </div>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving || selected === current}
            data-testid="display-currency-save-btn"
            className="sm:w-32"
          >
            {saving ? 'Saving…' : selected === current ? <><Check className="w-4 h-4 mr-1" /> Saved</> : 'Apply'}
          </Button>
        </div>
        <div className="mt-3 text-xs sm:text-sm" data-testid="display-currency-source-note">
          {fxMeta?.available ? (
            <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200">
              <span className="text-emerald-500 mt-0.5" aria-hidden="true">●</span>
              <div>
                <div className="font-medium text-emerald-800">Live exchange rates connected</div>
                <div className="text-emerald-700 text-xs mt-0.5">
                  Exchange rates from <span className="font-medium">{fxMeta.source}</span> · as of {fmtDate(fxMeta.fetched_at)}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200" data-testid="fx-not-connected-note">
              <span className="text-amber-500 mt-0.5" aria-hidden="true">●</span>
              <div>
                <div className="font-medium text-amber-800">Exchange rates not connected</div>
                <div className="text-amber-700 text-xs mt-0.5">
                  Totals across multiple currencies are shown as separate per-currency rows on the Overview tab. Contact support to enable live conversion.
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DisplayCurrencySetting;
